import React, { useEffect, useRef, useState } from 'react';

import {
    Category,
    findLevelOneCategoryForCategoryId,
    ExpandedCategories,
    getPreExpandedCategories,
    CrossCampaignCategoryEntryView,
    findCategoryById,
} from '@browsing/filters';
import { ErrorBoundary } from '@browsing/components';
import { CategoryTabIds } from '@shared/constants';

import { getGA4Context, trackSelectFilter, trackViewFilter } from '@browsing/tracking';

import { useAllCategories, useCCFEntries } from '../hooks';
import { getSavedCategoryIds, redirectToCrossCampaignCatalogMultiCategorySelect } from '../utils';

interface CrossCampaignEntrySmallScreenComponentProps {
    categoryTabId: CategoryTabIds;
}

const CrossCampaignEntrySmallScreenComponent = ({
    categoryTabId,
}: CrossCampaignEntrySmallScreenComponentProps) => {
    const _body = useRef<HTMLElement>(null);
    const ccfEntriesTabs = useCCFEntries();
    const allCategories = useAllCategories();
    const [selectableCategories, setSelectableCategories] = useState<Category>();
    const [selectedLevelOneCategory, setSelectedLevelOneCategory] = useState<Category | null>(null);
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);

    /**
     * `preExpandedCategories` represents categories displayed as expanded in the UI without being selected.
     * For instance, if a level 3 category is selected, its parent categories at levels 1 and 2 will be expanded.
     *
     * Additionally, in the case of this CCF entry point, when a user clicks on any filter, no category is selected,
     * but the clicked category is expanded. This UI behavior is managed using `preExpandedCategories`.
     */

    const [preExpandedCategories, setPreExpandedCategories] = useState<ExpandedCategories>({
        level1: null,
        level2: null,
    });

    useEffect(() => {
        if (!_body.current) {
            _body.current = document.body;
        }

        const hasSelectedCategories = !!preExpandedCategories.level1;

        _body.current.style.overflowY =
            hasSelectedCategories && _body.current ? 'hidden' : 'scroll';
    }, [preExpandedCategories.level1]);

    const handleSelectedCategories = (categoryIds: number[]) => {
        const foundCategories = categoryIds
            .map(categoryId => findCategoryById({ category: allCategories, id: categoryId }))
            .filter(category => category !== null);

        setSelectedCategories(foundCategories);
        return foundCategories;
    };

    const restoreSavedCategoryState = () => {
        const savedCategoryIds = getSavedCategoryIds();

        if (!savedCategoryIds || !savedCategoryIds.length) {
            return;
        }

        const nextSelectedCategories = handleSelectedCategories(savedCategoryIds);

        if (!nextSelectedCategories.length) {
            return;
        }

        const levelOneCategoryForCategoryId = findLevelOneCategoryForCategoryId({
            categoryId: nextSelectedCategories[0].id,
            categories: allCategories?.categories,
        });

        const nextSelectableCategories = {
            ...allCategories,
            ...(levelOneCategoryForCategoryId && {
                categories: [levelOneCategoryForCategoryId],
            }),
        };

        setSelectableCategories(nextSelectableCategories);

        const nextPreExpandedCategories = getPreExpandedCategories({
            categoryIds: savedCategoryIds,
            categories: allCategories?.categories,
        });

        setPreExpandedCategories(nextPreExpandedCategories);
    };

    useEffect(() => {
        restoreSavedCategoryState();
    }, []);

    const resetSelectedCategories = () => {
        setSelectedCategories([]);
    };

    const resetPreExpandedCategories = () => {
        setPreExpandedCategories({ level1: null, level2: null });
    };

    const selectCategoryLevelOne = (categoryId: number) => {
        const nextSelectedCategory = findCategoryById({
            category: allCategories,
            id: categoryId,
        });

        setSelectedLevelOneCategory(nextSelectedCategory);

        if (!nextSelectedCategory) {
            return;
        }

        const isKidsTab = categoryTabId === CategoryTabIds.KIDS;

        trackSelectFilter({
            context: 'cross_campaign_filter',
            element: 'category_filter',
            filterLvl: isKidsTab ? 1 : 2,
            filtersValue: isKidsTab
                ? `${categoryId}`
                : `${nextSelectedCategory?.parentId};${categoryId}`,
        });

        const parentCategory = findCategoryById({
            category: allCategories,
            id: nextSelectedCategory.parentId,
        });

        const nextSelectableCategories = {
            ...allCategories,
            categories:
                allCategories?.categories?.filter(
                    ({ id }) => id === (isKidsTab ? categoryId : parentCategory?.id),
                ) || [],
        };

        /**
         * The kids tab behaves differently because there’s no explicit kids category.
         * Instead, it combines the boys and girls categories.
         * Users must select either boys or girls (level 1) before choosing a level 2 category.
         */

        const nextPreExpandedCategories = isKidsTab
            ? { level1: categoryId, level2: null }
            : {
                  level1: parentCategory?.id,
                  level2: categoryId,
              };

        setPreExpandedCategories(nextPreExpandedCategories);

        setSelectableCategories(nextSelectableCategories);
    };

    useEffect(() => {
        window.scrollTo({ top: 0 });

        trackViewFilter({
            context: getGA4Context({ isCrossCampaign: true }),
            element: 'category_filter',
            filterLvl: 2,
            filtersValue: '',
        });
    }, [categoryTabId]);

    if (!ccfEntriesTabs || !ccfEntriesTabs.length) {
        return null;
    }

    const tab = ccfEntriesTabs?.find(({ tabId }) => tabId === categoryTabId);

    return (
        <CrossCampaignCategoryEntryView
            tab={tab || null}
            selectedLevelOneCategory={selectedLevelOneCategory}
            preExpandedCategories={preExpandedCategories}
            allCategories={allCategories}
            selectableCategories={selectableCategories}
            selectedCategories={selectedCategories}
            onSelectCategory={selectCategoryLevelOne}
            onResetSelectedCategories={resetSelectedCategories}
            onHandleSelectedCategories={handleSelectedCategories}
            onResetPreExpandedCategories={resetPreExpandedCategories}
            onSetSelectedCategory={setSelectedLevelOneCategory}
            onRedirectToCatalog={redirectToCrossCampaignCatalogMultiCategorySelect}
        />
    );
};

export const CrossCampaignEntrySmallScreen = (
    props: CrossCampaignEntrySmallScreenComponentProps,
) => (
    <ErrorBoundary componentName="cross-campaign-entry-small-screen">
        <CrossCampaignEntrySmallScreenComponent {...props} />
    </ErrorBoundary>
);
